'use client'

import React, {createContext, useContext, useEffect} from 'react'
import {toast} from 'sonner'
import {FitroToaster} from '../ui/Toast'
import type {NotifyOptions, ToastVariant} from '../ui/Toast'
import {resolveToastDuration} from '../ui/Toast/duration'
import {captureException} from '../../lib/client-logger'
import {reportMissingNotificationsProvider} from '../../lib/clientErrorReporter'
import {registerSessionExpiredHandler} from '../../lib/session-expired'
import {isPublicTenantEntryPath} from '../../lib/tenant-seo-policy'
import {useTranslation} from '../../hooks/useTranslation'

const isDebugEnv = process.env.NEXT_PUBLIC_APP_ENV === 'dev' || process.env.NODE_ENV === 'development'

export interface NotificationsValue {
    notifySuccess: (message: React.ReactNode, options?: NotifyOptions) => string
    notifyError: (message: React.ReactNode, options?: NotifyOptions) => string
    notifyWarning: (message: React.ReactNode, options?: NotifyOptions) => string
    notifyInfo: (message: React.ReactNode, options?: NotifyOptions) => string
    notify: (variant: ToastVariant, message: React.ReactNode, options?: NotifyOptions) => string
    dismissToast: (id: string) => void
}

const NotificationsContext = createContext<NotificationsValue | undefined>(undefined)
const MISSING_PROVIDER_MESSAGE = 'useNotifications must be used within NotificationsProvider'
const NOOP_NOTIFICATIONS: NotificationsValue = {
    notifySuccess: () => 'noop',
    notifyError: () => 'noop',
    notifyWarning: () => 'noop',
    notifyInfo: () => 'noop',
    notify: () => 'noop',
    dismissToast: () => undefined
}

// Identical repeats update the visible toast instead of stacking a copy.
const deriveDedupeKey = (variant: ToastVariant, message: React.ReactNode, options?: NotifyOptions): string | undefined => {
    if (options?.dedupeKey) return options.dedupeKey
    if (typeof message === 'string') {
        const trimmed = message.trim()
        return trimmed ? `${variant}:${trimmed}` : undefined
    }
    if (typeof message === 'number' || typeof message === 'boolean') {
        return `${variant}:${String(message)}`
    }
    return undefined
}

const runAction = (onAction: () => void | Promise<void>) => {
    Promise.resolve().then(onAction).catch((error: unknown) => {
        captureException(error instanceof Error ? error : new Error('Toast action failed'), {
            event: 'toast_action_failed',
            source: 'toast',
            message: 'Toast action failed',
        })
    })
}

const notify = (variant: ToastVariant, message: React.ReactNode, options?: NotifyOptions): string => {
    const durationMs = resolveToastDuration(variant, message, options)
    const onAction = options?.onAction
    return String(toast[variant](message, {
        id: deriveDedupeKey(variant, message, options),
        duration: durationMs > 0 ? durationMs : Infinity,
        action: options?.actionLabel && onAction
            ? {label: options.actionLabel, onClick: () => runAction(onAction)}
            : undefined
    }))
}

const NOTIFICATIONS: NotificationsValue = {
    notify,
    notifySuccess: (message, options) => notify('success', message, options),
    notifyError: (message, options) => notify('error', message, options),
    notifyWarning: (message, options) => notify('warning', message, options),
    notifyInfo: (message, options) => notify('info', message, options),
    dismissToast: id => {
        toast.dismiss(id)
    }
}

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const {t} = useTranslation('notifications')

    useEffect(() => {
        return registerSessionExpiredHandler(() => {
            const pathname = window.location.pathname
            if (pathname.startsWith('/sign-in') || pathname.startsWith('/sign-up')) return
            if (isPublicTenantEntryPath(pathname)) return
            const redirectUrl = `${window.location.pathname}${window.location.search}`
            NOTIFICATIONS.notifyError(t('sessionExpired'), {dedupeKey: 'session-expired'})
            window.setTimeout(() => {
                window.location.assign(
                    new URL(`/sign-in?redirect_url=${encodeURIComponent(redirectUrl)}`, window.location.origin),
                )
            }, 1200)
        })
    }, [t])

    return (
        <NotificationsContext.Provider value={NOTIFICATIONS}>
            {children}
            <FitroToaster/>
        </NotificationsContext.Provider>
    )
}

export const useNotifications = (): NotificationsValue => {
    const ctx = useContext(NotificationsContext)
    if (!ctx) {
        reportMissingNotificationsProvider()
        if (isDebugEnv) {
            throw new Error(MISSING_PROVIDER_MESSAGE)
        }
        return NOOP_NOTIFICATIONS
    }
    return ctx
}
